import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Table, Input, Button, Row, Col } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { api } from "../utils";
import { ui_helpers } from "../helpers";
import Alert from "react-s-alert-v3";
import _ from "lodash";
import { Loading } from "./";

const SENSITIVE_FIELD_NAMES = ["Ss1"];

export default function RTOProExportReviewModal({ contractId, isOpen, onClose, onExported }) {
  const [fields, setFields] = useState(null);
  const [canViewSensitiveFields, setCanViewSensitiveFields] = useState(false);
  const [revealedFieldNames, setRevealedFieldNames] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [cultureOptions, setCultureOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    if (!isOpen || !contractId) return;
    setFields(null);
    setRevealedFieldNames([]);
    api
      .fetch(`contract/PreviewRTOProExport/${contractId}`)
      .then((r) => {
        if (r.data.success) {
          setFields(r.data.message.fields);
          setCanViewSensitiveFields(r.data.message.canViewSensitiveFields);
        } else {
          Alert.error(r.data.message);
          onClose();
        }
      })
      .catch((e) => {
        console.error(e);
        Alert.error("There was an error loading the RTOPro export preview.");
        onClose();
      });
    api
      .fetch("reference/CultureList")
      .then((r) => {
        if (r.data.success) setCultureOptions(ui_helpers.idNameToValueLabel(r.data.data, null, false));
      })
      .catch((e) => console.error(e));
    api
      .fetch("reference/FullRegionList")
      .then((r) => {
        if (r.data.success) {
          const options = _.chain(r.data.data)
            .uniqBy("abbr")
            .map((region) => ({ value: region.abbr, label: region.name }))
            .sortBy("label")
            .value();
          setStateOptions(options);
        }
      })
      .catch((e) => console.error(e));
  }, [isOpen, contractId]);

  const dropdownConfigByField = {
    Language: { options: cultureOptions, matchKey: "label" },
    State: { options: stateOptions, matchKey: "value" },
    DelState: { options: stateOptions, matchKey: "value" },
  };

  function onChange(fieldName, value) {
    setFields((prev) => prev.map((f) => (f.fieldName === fieldName ? { ...f, value } : f)));
  }

  function toggleRevealed(fieldName) {
    setRevealedFieldNames((prev) =>
      prev.includes(fieldName) ? prev.filter((n) => n !== fieldName) : [...prev, fieldName]
    );
  }

  function onExport() {
    setIsSaving(true);
    api
      .post("contract/ExportToRTOPro", { contractId, fields })
      .then((r) => {
        if (r.data.success) {
          onExported();
        } else {
          Alert.error(r.data.message);
        }
      })
      .catch((e) => {
        console.error(e);
        Alert.error("There was an error exporting to RTOPro.");
      })
      .finally(() => setIsSaving(false));
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Review RTOPro Export</ModalHeader>
      <ModalBody>
        {!fields ? (
          <Loading />
        ) : (
          <>
            <p className="text-muted" style={{ fontSize: "13px" }}>
              Review and correct the data below before exporting.{" "}
              <FontAwesomeIcon icon="save" className="mx-1" />
              = also updates this contract's record, not just this export.
            </p>
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <Table size="sm" bordered>
                <tbody>
                  {_.map(fields, (f) => {
                    const isSensitive = SENSITIVE_FIELD_NAMES.includes(f.fieldName);
                    const isRevealed = revealedFieldNames.includes(f.fieldName);
                    const dropdownConfig = dropdownConfigByField[f.fieldName];
                    return (
                      <tr key={f.fieldName}>
                        <td style={{ width: "35%", verticalAlign: "middle", backgroundColor: "#000", color: "#fff" }}>
                          {f.label}
                          {f.persisted ? (
                            <FontAwesomeIcon icon="save" className="ms-2" title="Also updates this contract's record" />
                          ) : null}
                        </td>
                        <td>
                          {dropdownConfig ? (
                            <Select
                              options={dropdownConfig.options}
                              value={_.find(dropdownConfig.options, (o) => o[dropdownConfig.matchKey] === f.value) || null}
                              onChange={(opt) => onChange(f.fieldName, opt[dropdownConfig.matchKey])}
                            />
                          ) : (
                            <Row className="align-items-center g-2">
                              <Col>
                                <Input
                                  type={isSensitive && !isRevealed ? "password" : "text"}
                                  value={f.value || ""}
                                  disabled={!f.editable}
                                  title={f.editable ? null : ""}
                                  onChange={(e) => onChange(f.fieldName, e.target.value)}
                                />
                              </Col>
                              {isSensitive && canViewSensitiveFields ? (
                                <Col xs="auto">
                                  <Button size="sm" color="dark" onClick={() => toggleRevealed(f.fieldName)}>
                                    {isRevealed ? "Hide" : "Show"}
                                  </Button>
                                </Col>
                              ) : null}
                            </Row>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button color="success" className="text-light" onClick={onExport} disabled={!fields || isSaving}>
          {isSaving ? "Exporting..." : "Export"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
